import React, { useState, useEffect } from "react";
import {
  X, User, Mail, Building, Shield, KeyRound, Lock, CheckCircle2,
  XCircle, LogOut, Smartphone, Globe, Calendar, RefreshCw, Eye,
  EyeOff, AlertCircle, Info, ShieldCheck, Check, Sparkles
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

const ROLE_DESCRIPTIONS = {
  owner: {
    title: "Portfolio Owner",
    badge: "OWNER",
    badgeClass: "bg-teal-500/15 text-teal-400 border-teal-500/30",
    desc: "Full administrative, financial, and team authority over this portfolio and its entities.",
  },
  editor: {
    title: "Portfolio Editor",
    badge: "EDITOR",
    badgeClass: "bg-blue-500/15 text-blue-400 border-blue-500/30",
    desc: "Operational access to manage properties, record transactions, and prepare tax documents.",
  },
  viewer: {
    title: "Portfolio Viewer",
    badge: "VIEWER",
    badgeClass: "bg-slate-500/15 text-slate-400 border-slate-500/30",
    desc: "Read-only access to view financial ledgers, property details, and performance metrics.",
  },
};

const PERMISSIONS_MATRIX = [
  {
    label: "Manage Properties & Units",
    owner: true,
    editor: true,
    viewer: false,
    desc: "Add, modify, and archive properties and rental units",
  },
  {
    label: "Record Transactions & Expenses",
    owner: true,
    editor: true,
    viewer: false,
    desc: "Log rent, operating expenses, and vendor payments",
  },
  {
    label: "Upload & Categorize Receipts",
    owner: true,
    editor: true,
    viewer: false,
    desc: "Attach receipt documents and apply IRS tax schedules",
  },
  {
    label: "Export Reports & CPA Packets",
    owner: true,
    editor: true,
    viewer: false,
    desc: "Generate Schedule E summaries and financial statements",
  },
  {
    label: "Invite & Manage Team Members",
    owner: true,
    editor: false,
    viewer: false,
    desc: "Add co-hosts, accountants, and manage permissions",
  },
  {
    label: "Rename Company Portfolio",
    owner: true,
    editor: false,
    viewer: false,
    desc: "Change organization name and primary display branding",
  },
  {
    label: "Manage Legal Entities & Bank Sync",
    owner: true,
    editor: false,
    viewer: false,
    desc: "Configure LLCs, tax entities, and linked bank accounts",
  },
  {
    label: "Delete Records & Sensitive Data",
    owner: true,
    editor: false,
    viewer: false,
    desc: "Permanently delete properties, logs, and financial records",
  },
];

export default function UserProfileModal({ open, onClose }) {
  const { user, account, role, updateProfile, changePassword, revokeOtherSessions, logout } = useAuth() || {};
  const [activeTab, setActiveTab] = useState("profile"); // 'profile' | 'security' | 'permissions'

  // Profile tab fields
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);

  // Security tab fields
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPass, setShowCurrentPass] = useState(false);
  const [showNewPass, setShowNewPass] = useState(false);
  const [changingPass, setChangingPass] = useState(false);
  const [revokingSessions, setRevokingSessions] = useState(false);

  useEffect(() => {
    if (open) {
      document.body.classList.add("has-modal-open");
      setUsername(user?.username || "");
      setEmail(user?.email || "");
      setCompanyName(account?.name || "");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } else {
      document.body.classList.remove("has-modal-open");
    }
    return () => {
      document.body.classList.remove("has-modal-open");
    };
  }, [open, user, account]);

  // Handle escape key
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const currentRole = role || user?.role || "viewer";
  const roleInfo = ROLE_DESCRIPTIONS[currentRole] || ROLE_DESCRIPTIONS.viewer;
  const isOwner = currentRole === "owner";

  const formatDate = (isoString) => {
    if (!isoString) return "N/A";
    try {
      const d = new Date(isoString);
      return d.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return isoString;
    }
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return "N/A";
    try {
      const d = new Date(isoString);
      return d.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
    } catch {
      return isoString;
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    const trimmedUser = username.trim();
    const trimmedEmail = email.trim();
    const trimmedCompany = companyName.trim();

    if (!trimmedUser) {
      toast.error("Username cannot be empty");
      return;
    }
    if (trimmedUser.length < 2 || trimmedUser.length > 50) {
      toast.error("Username must be between 2 and 50 characters");
      return;
    }
    if (!/^[a-zA-Z0-9_\-\.]+$/.test(trimmedUser)) {
      toast.error("Username may only contain letters, numbers, dots, dashes, and underscores");
      return;
    }
    if (trimmedEmail && !trimmedEmail.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }

    try {
      setSavingProfile(true);
      await updateProfile({
        username: trimmedUser,
        email: trimmedEmail || null,
        companyName: isOwner ? trimmedCompany : undefined,
      });
      toast.success("Profile updated successfully");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update profile");
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!currentPassword) {
      toast.error("Please enter your current password");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("New password must be at least 6 characters long");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("New password and confirmation do not match");
      return;
    }

    try {
      setChangingPass(true);
      await changePassword({ currentPassword, newPassword });
      toast.success("Password updated! Please log in with your new credentials.");
      onClose();
      await logout();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change password");
    } finally {
      setChangingPass(false);
    }
  };

  const handleRevokeOtherSessions = async () => {
    if (!window.confirm("Are you sure you want to sign out of all other devices?")) return;
    try {
      setRevokingSessions(true);
      await revokeOtherSessions();
      toast.success("All other active sessions have been signed out.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to revoke sessions");
    } finally {
      setRevokingSessions(false);
    }
  };

  const userInitial = (user?.username || "U").charAt(0).toUpperCase();

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-950/70 backdrop-blur-md animate-in fade-in duration-150"
      onClick={onClose}
      data-testid="user-profile-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="user-profile-modal-title"
    >
      <div
        className="bg-[var(--surface)] border border-[var(--border)] rounded-2xl max-w-xl w-full max-h-[92vh] flex flex-col shadow-2xl overflow-hidden animate-in zoom-in-95 duration-150"
        onClick={(e) => e.stopPropagation()}
        data-testid="user-profile-modal"
      >
        {/* Header */}
        <div className="px-6 py-5 border-b border-[var(--border)] flex items-center justify-between gap-3 shrink-0 bg-gradient-to-r from-[var(--surface)] to-[var(--surface-hover)]">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-teal-500 via-teal-600 to-[#1C3A5E] text-white flex items-center justify-center font-black text-lg shadow-md border border-teal-400/30 shrink-0">
              {userInitial}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h2 id="user-profile-modal-title" className="text-base sm:text-lg font-black tracking-tight text-[var(--text)] truncate">
                  {user?.username || "Account Profile"}
                </h2>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider border shrink-0 ${roleInfo.badgeClass}`}>
                  {roleInfo.badge}
                </span>
              </div>
              <p className="text-xs text-[var(--muted)] truncate mt-0.5">
                {account?.name ? `${account.name} • ` : ""}User ID: <span className="font-mono">{user?.id?.slice(0, 8) || "N/A"}</span>
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="user-profile-modal-close"
            className="w-8 h-8 rounded-xl border border-[var(--border)] text-[var(--muted)] hover:text-[var(--text)] hover:bg-[var(--surface-hover)] flex items-center justify-center transition-colors shrink-0"
            aria-label="Close profile modal"
          >
            <X size={16} />
          </button>
        </div>

        {/* Tab Selector */}
        <div className="flex items-center gap-1 px-6 pt-3 border-b border-[var(--border)] bg-[var(--surface)]/50 shrink-0 overflow-x-auto no-scrollbar">
          <button
            type="button"
            onClick={() => setActiveTab("profile")}
            data-testid="tab-profile-details"
            className={`px-3.5 py-2 text-xs font-bold rounded-t-xl transition-all border-b-2 flex items-center gap-2 whitespace-nowrap ${
              activeTab === "profile"
                ? "border-teal-500 text-teal-500 bg-teal-500/10"
                : "border-transparent text-[var(--muted)] hover:text-[var(--text)] hover:bg-[var(--surface-hover)]"
            }`}
          >
            <User size={14} />
            <span>Profile & Details</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("security")}
            data-testid="tab-security"
            className={`px-3.5 py-2 text-xs font-bold rounded-t-xl transition-all border-b-2 flex items-center gap-2 whitespace-nowrap ${
              activeTab === "security"
                ? "border-teal-500 text-teal-500 bg-teal-500/10"
                : "border-transparent text-[var(--muted)] hover:text-[var(--text)] hover:bg-[var(--surface-hover)]"
            }`}
          >
            <KeyRound size={14} />
            <span>Security & Sessions</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("permissions")}
            data-testid="tab-permissions"
            className={`px-3.5 py-2 text-xs font-bold rounded-t-xl transition-all border-b-2 flex items-center gap-2 whitespace-nowrap ${
              activeTab === "permissions"
                ? "border-teal-500 text-teal-500 bg-teal-500/10"
                : "border-transparent text-[var(--muted)] hover:text-[var(--text)] hover:bg-[var(--surface-hover)]"
            }`}
          >
            <ShieldCheck size={14} />
            <span>Role & Permissions</span>
          </button>
        </div>

        {/* Modal Body (Scrollable) */}
        <div className="p-6 overflow-y-auto space-y-6 flex-1 text-sm">
          {/* TAB 1: Profile & Details */}
          {activeTab === "profile" && (
            <form onSubmit={handleSaveProfile} className="space-y-4" data-testid="profile-form">
              <div>
                <label className="block text-xs font-bold text-[var(--text)] mb-1.5 flex items-center gap-1.5">
                  <User size={13} className="text-teal-400" />
                  <span>Username</span>
                </label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="e.g. johndoe"
                  data-testid="profile-username-input"
                  className="w-full px-3.5 py-2 rounded-xl border border-[var(--border)] bg-[var(--surface)] text-[var(--text)] text-xs font-medium focus:outline-none focus:border-teal-500 transition-colors shadow-sm"
                  required
                />
                <p className="text-[11px] text-[var(--muted)] mt-1">
                  Unique sign-in identifier across your properties. Letters, numbers, hyphens, and dots allowed.
                </p>
              </div>

              <div>
                <label className="block text-xs font-bold text-[var(--text)] mb-1.5 flex items-center gap-1.5">
                  <Mail size={13} className="text-teal-400" />
                  <span>Email Address</span>
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="e.g. user@example.com"
                  data-testid="profile-email-input"
                  className="w-full px-3.5 py-2 rounded-xl border border-[var(--border)] bg-[var(--surface)] text-[var(--text)] text-xs font-medium focus:outline-none focus:border-teal-500 transition-colors shadow-sm"
                />
                <p className="text-[11px] text-[var(--muted)] mt-1">
                  Used for account notifications, CPA review comments, and password recovery.
                </p>
              </div>

              <div>
                <label className="block text-xs font-bold text-[var(--text)] mb-1.5 flex items-center justify-between">
                  <span className="flex items-center gap-1.5">
                    <Building size={13} className="text-teal-400" />
                    <span>Company / Portfolio Name</span>
                  </span>
                  {!isOwner && (
                    <span className="text-[10px] font-semibold text-amber-400 flex items-center gap-1">
                      <Lock size={10} /> Owner Only
                    </span>
                  )}
                </label>
                <input
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  disabled={!isOwner}
                  placeholder="e.g. Lapen Holdings LLC"
                  data-testid="profile-company-input"
                  className={`w-full px-3.5 py-2 rounded-xl border text-xs font-medium focus:outline-none transition-colors shadow-sm ${
                    isOwner
                      ? "border-[var(--border)] bg-[var(--surface)] text-[var(--text)] focus:border-teal-500"
                      : "border-[var(--border)] bg-[var(--surface-hover)] text-[var(--muted)] cursor-not-allowed opacity-80"
                  }`}
                />
                <p className="text-[11px] text-[var(--muted)] mt-1">
                  {isOwner
                    ? "As Portfolio Owner, updating this updates the display title across all dashboards and reports."
                    : "Only the portfolio owner can change the organization name."}
                </p>
              </div>

              {/* Readonly Metadata Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                <div className="p-3 rounded-xl border border-[var(--border)] bg-[var(--surface-hover)]/60">
                  <div className="flex items-center gap-2 text-[var(--muted)] text-[11px] font-semibold">
                    <Shield size={12} />
                    <span>Assigned Role</span>
                  </div>
                  <div className="mt-1 text-xs font-bold text-[var(--text)] flex items-center gap-2">
                    <span>{roleInfo.title}</span>
                    <span className={`px-1.5 py-0.2 rounded text-[9px] font-black uppercase border ${roleInfo.badgeClass}`}>
                      {roleInfo.badge}
                    </span>
                  </div>
                </div>

                <div className="p-3 rounded-xl border border-[var(--border)] bg-[var(--surface-hover)]/60">
                  <div className="flex items-center gap-2 text-[var(--muted)] text-[11px] font-semibold">
                    <Calendar size={12} />
                    <span>Member Since</span>
                  </div>
                  <div className="mt-1 text-xs font-bold text-[var(--text)]">
                    {formatDate(user?.created_at || account?.created_at)}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-4 border-t border-[var(--border)]">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 rounded-xl border border-[var(--border)] text-xs font-bold text-[var(--muted)] hover:text-[var(--text)] hover:bg-[var(--surface-hover)] transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingProfile}
                  data-testid="save-profile-button"
                  className="px-5 py-2 rounded-xl bg-teal-500 hover:bg-teal-400 disabled:opacity-50 text-slate-950 text-xs font-black transition-colors shadow-sm flex items-center gap-1.5"
                >
                  {savingProfile ? (
                    <>
                      <RefreshCw size={13} className="animate-spin" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <Check size={14} />
                      <span>Save Changes</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          )}

          {/* TAB 2: Security & Credentials */}
          {activeTab === "security" && (
            <div className="space-y-6" data-testid="security-section">
              {/* Change Password */}
              <form onSubmit={handleChangePassword} className="space-y-4">
                <div>
                  <h4 className="text-xs font-black uppercase tracking-wider text-[var(--text)] flex items-center gap-1.5">
                    <KeyRound size={13} className="text-teal-400" />
                    <span>Change Password</span>
                  </h4>
                  <p className="text-[11px] text-[var(--muted)] mt-0.5">
                    Choose a strong password with at least 6 characters. You will need to sign in again after changing it.
                  </p>
                </div>

                <div>
                  <label className="block text-xs font-bold text-[var(--text)] mb-1">Current Password</label>
                  <div className="relative">
                    <input
                      type={showCurrentPass ? "text" : "password"}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="Enter current password"
                      data-testid="current-password-input"
                      className="w-full pl-3.5 pr-10 py-2 rounded-xl border border-[var(--border)] bg-[var(--surface)] text-[var(--text)] text-xs font-medium focus:outline-none focus:border-teal-500 transition-colors shadow-sm"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPass(!showCurrentPass)}
                      className="absolute right-3 top-2.5 text-[var(--muted)] hover:text-[var(--text)]"
                    >
                      {showCurrentPass ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-[var(--text)] mb-1">New Password</label>
                    <div className="relative">
                      <input
                        type={showNewPass ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="At least 6 chars"
                        data-testid="new-password-input"
                        className="w-full pl-3.5 pr-10 py-2 rounded-xl border border-[var(--border)] bg-[var(--surface)] text-[var(--text)] text-xs font-medium focus:outline-none focus:border-teal-500 transition-colors shadow-sm"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPass(!showNewPass)}
                        className="absolute right-3 top-2.5 text-[var(--muted)] hover:text-[var(--text)]"
                      >
                        {showNewPass ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-[var(--text)] mb-1">Confirm New Password</label>
                    <input
                      type={showNewPass ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Repeat new password"
                      data-testid="confirm-password-input"
                      className="w-full px-3.5 py-2 rounded-xl border border-[var(--border)] bg-[var(--surface)] text-[var(--text)] text-xs font-medium focus:outline-none focus:border-teal-500 transition-colors shadow-sm"
                      required
                    />
                  </div>
                </div>

                <div className="flex justify-end pt-2">
                  <button
                    type="submit"
                    disabled={changingPass || !currentPassword || !newPassword}
                    data-testid="submit-password-button"
                    className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-white disabled:opacity-50 text-xs font-bold transition-colors shadow-sm flex items-center gap-1.5"
                  >
                    {changingPass ? (
                      <>
                        <RefreshCw size={13} className="animate-spin" />
                        <span>Updating...</span>
                      </>
                    ) : (
                      <>
                        <KeyRound size={13} />
                        <span>Update Password</span>
                      </>
                    )}
                  </button>
                </div>
              </form>

              {/* Active Sessions & Security Info */}
              <div className="pt-4 border-t border-[var(--border)] space-y-3">
                <div>
                  <h4 className="text-xs font-black uppercase tracking-wider text-[var(--text)] flex items-center gap-1.5">
                    <Shield size={13} className="text-teal-400" />
                    <span>Session & Device Activity</span>
                  </h4>
                  <p className="text-[11px] text-[var(--muted)] mt-0.5">
                    Review recent sign-in telemetry and manage active sessions across all devices.
                  </p>
                </div>

                <div className="p-3.5 rounded-xl border border-[var(--border)] bg-[var(--surface-hover)]/40 space-y-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-[var(--muted)] flex items-center gap-1.5">
                      <Globe size={13} className="text-teal-400" />
                      <span>Last Sign-In IP</span>
                    </span>
                    <span className="font-mono font-bold text-[var(--text)]">
                      {user?.last_login_ip || "Current Device"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-[var(--muted)] flex items-center gap-1.5">
                      <Calendar size={13} className="text-teal-400" />
                      <span>Last Activity Timestamp</span>
                    </span>
                    <span className="font-medium text-[var(--text)]">
                      {formatDateTime(user?.last_login)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-[var(--muted)] flex items-center gap-1.5">
                      <Smartphone size={13} className="text-teal-400" />
                      <span>Total Logins</span>
                    </span>
                    <span className="font-bold text-[var(--text)]">
                      {user?.login_count || 1}
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 rounded-xl border border-red-500/20 bg-red-500/5">
                  <div className="text-xs">
                    <div className="font-bold text-[var(--text)]">Sign out of other devices</div>
                    <div className="text-[11px] text-[var(--muted)]">Revokes all tokens except your current browser</div>
                  </div>
                  <button
                    type="button"
                    onClick={handleRevokeOtherSessions}
                    disabled={revokingSessions}
                    data-testid="revoke-other-sessions-button"
                    className="px-3 py-1.5 rounded-lg border border-red-500/40 text-red-400 hover:bg-red-500/10 text-xs font-bold transition-colors flex items-center gap-1.5"
                  >
                    {revokingSessions ? <RefreshCw size={12} className="animate-spin" /> : <LogOut size={12} />}
                    <span>Revoke Others</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* TAB 3: Role & Permissions */}
          {activeTab === "permissions" && (
            <div className="space-y-4" data-testid="permissions-section">
              {/* Role Card */}
              <div className="p-4 rounded-xl border border-teal-500/30 bg-gradient-to-br from-teal-500/10 via-[var(--surface)] to-transparent">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-teal-500/20 border border-teal-500/30 text-teal-400 flex items-center justify-center">
                      <ShieldCheck size={18} />
                    </div>
                    <div>
                      <h4 className="text-sm font-black text-[var(--text)]">{roleInfo.title}</h4>
                      <p className="text-[11px] text-[var(--muted)]">Tier: {roleInfo.badge}</p>
                    </div>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider border shrink-0 ${roleInfo.badgeClass}`}>
                    Current Role
                  </span>
                </div>
                <p className="text-xs text-[var(--text)] mt-2.5 leading-relaxed">
                  {roleInfo.desc}
                </p>
              </div>

              {/* Permissions Breakdown Matrix */}
              <div>
                <h4 className="text-xs font-black uppercase tracking-wider text-[var(--text)] mb-2 flex items-center gap-1.5">
                  <Sparkles size={13} className="text-teal-400" />
                  <span>Feature Access & Capabilities</span>
                </h4>
                <div className="border border-[var(--border)] rounded-xl divide-y divide-[var(--border)] overflow-hidden bg-[var(--surface-hover)]/30">
                  {PERMISSIONS_MATRIX.map((perm, idx) => {
                    const hasAccess =
                      currentRole === "owner"
                        ? perm.owner
                        : currentRole === "editor"
                        ? perm.editor
                        : perm.viewer;

                    return (
                      <div
                        key={idx}
                        className="px-3.5 py-2.5 flex items-center justify-between gap-3 text-xs"
                      >
                        <div className="min-w-0">
                          <div className={`font-bold ${hasAccess ? "text-[var(--text)]" : "text-[var(--muted)]"}`}>
                            {perm.label}
                          </div>
                          <div className="text-[11px] text-[var(--muted)] truncate">
                            {perm.desc}
                          </div>
                        </div>
                        <div className="shrink-0 flex items-center gap-1.5">
                          {hasAccess ? (
                            <span className="flex items-center gap-1 text-[11px] font-bold text-teal-400 bg-teal-500/10 px-2 py-0.5 rounded-full border border-teal-500/20">
                              <CheckCircle2 size={12} />
                              <span>Allowed</span>
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-[11px] font-semibold text-slate-500 bg-slate-500/10 px-2 py-0.5 rounded-full border border-slate-500/20">
                              <XCircle size={12} />
                              <span>Restricted</span>
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {!isOwner && (
                <div className="p-3 rounded-xl border border-[var(--border)] bg-[var(--surface-hover)]/60 text-xs text-[var(--muted)] flex items-start gap-2.5">
                  <Info size={16} className="text-teal-400 shrink-0 mt-0.5" />
                  <p className="leading-relaxed text-[11px]">
                    Need elevated permissions to manage legal entities or team members? Please contact your Portfolio Owner to adjust your account role.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
